import { useContext, useEffect, useMemo, useState, type JSX } from 'react';
import { OrganizationSwitch, type OrganizationSwitchInvitation } from '@urbanmetrix/um-kit';
import { AuthContext, ViewContext } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import { CreateOrganizationModal } from '../../views/add-organization/CreateOrganizationModal';
import {
  acceptOrganizationInvitation,
  getActiveOrganization,
  listOrganizations,
  listAdminOrganizations,
  listUserOrganizationInvitations,
  setActiveOrganization,
  setAdminActiveOrganization,
} from '../../lib/auth-client';

type Organization = {
  id: string | number;
  name: string;
};

type OrganizationInvitation = {
  id: string;
  organizationId: string;
  organizationName: string;
  role?: string | null;
  status: string;
};

type AuthContextShape = {
  isAuthenticated?: boolean;
  refreshSession?: () => void | Promise<unknown>;
  permission?: {
    owner?: boolean;
  };
  user?: {
    email?: string | null;
    name?: string | null;
    appRole?: string | null;
    role?: string | null;
    organizations?: Array<{ id: string | number; name?: string | null }>;
    session?: {
      activeOrganizationId?: string | null;
    } | null;
  } | null;
};

type ViewContextShape = {
  setLoading?: (value: boolean) => void;
  notification?: {
    show: (message: string, type: string, dismissible?: boolean, display?: string) => void;
  };
};

type UserOrganizationSwitchProps = {
  className?: string;
  nameClassName?: string;
  separatorClassName?: string;
  organizationClassName?: string;
  switchButtonClassName?: string;
};

export function UserOrganizationSwitch({
  className,
  nameClassName,
  switchButtonClassName,
}: UserOrganizationSwitchProps): JSX.Element {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext) as AuthContextShape;
  const viewContext = useContext(ViewContext) as ViewContextShape;
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrganization, setActiveOrganizationDetails] = useState<Organization | null>(null);
  const [guestOrganizations, setGuestOrganizations] = useState<Organization[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [isOrganizationsLoading, setIsOrganizationsLoading] = useState(false);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const [acceptingInvitationId, setAcceptingInvitationId] = useState<string | null>(null);
  const [createOrganizationOpen, setCreateOrganizationOpen] = useState(false);
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;
  const canSwitchById = ['dev', 'support'].includes(String(authContext.user?.appRole ?? authContext.user?.role ?? ''));

  useEffect(() => {
    let cancelled = false;

    async function loadOrganizations() {
      if (!authContext.isAuthenticated) {
        setOrganizations([]);
        setActiveOrganizationDetails(null);
        setGuestOrganizations([]);
        setInvitations([]);
        return;
      }

      try {
        setIsOrganizationsLoading(true);
        const [organizationsResult, activeOrganizationResult, adminOrganizationsResult, invitationsResult] =
          await Promise.allSettled([
            listOrganizations(),
            activeOrganizationId ? getActiveOrganization() : Promise.resolve(null),
            canSwitchById ? listAdminOrganizations() : Promise.resolve([]),
            listUserOrganizationInvitations(),
          ]);
        if (!cancelled) {
          const nextOrganizations = organizationsResult.status === 'fulfilled' ? organizationsResult.value : [];
          const nextActiveOrganization =
            activeOrganizationResult.status === 'fulfilled' ? activeOrganizationResult.value : null;
          const allOrganizations =
            adminOrganizationsResult.status === 'fulfilled' ? adminOrganizationsResult.value : [];
          const nextInvitations = invitationsResult.status === 'fulfilled' ? invitationsResult.value : [];
          const memberOrganizations = Array.isArray(nextOrganizations)
            ? nextOrganizations.map((organization) => ({
                ...organization,
                name: organization?.name || 'Organization',
              }))
            : [];
          const normalizedActiveOrganization =
            nextActiveOrganization && 'id' in nextActiveOrganization
              ? {
                  id: nextActiveOrganization.id,
                  name: nextActiveOrganization.name || 'Organization',
                }
              : null;

          if (
            normalizedActiveOrganization &&
            !memberOrganizations.some(
              (organization) => String(organization.id) === String(normalizedActiveOrganization.id)
            )
          ) {
            memberOrganizations.unshift(normalizedActiveOrganization);
          }
          const memberOrganizationIds = new Set(memberOrganizations.map((organization) => String(organization.id)));

          setOrganizations(memberOrganizations);
          setActiveOrganizationDetails(normalizedActiveOrganization);
          setGuestOrganizations(
            Array.isArray(allOrganizations)
              ? allOrganizations
                  .filter((organization) => !memberOrganizationIds.has(String(organization.id)))
                  .map((organization) => ({
                    ...organization,
                    name: organization?.name || 'Organization',
                  }))
              : []
          );
          setInvitations(
            Array.isArray(nextInvitations)
              ? nextInvitations.filter((invitation) => invitation.status === 'pending')
              : []
          );
        }
      } catch (error) {
        if (!cancelled) {
          setActiveOrganizationDetails(null);
        }
      } finally {
        if (!cancelled) {
          setIsOrganizationsLoading(false);
        }
      }
    }

    loadOrganizations();

    return () => {
      cancelled = true;
    };
  }, [authContext.isAuthenticated, activeOrganizationId, canSwitchById]);

  const currentOrganization = useMemo(() => {
    const organization =
      [...organizations, ...guestOrganizations].find(
        (organization) => String(organization.id) === String(activeOrganizationId)
      ) ||
      activeOrganization ||
      authContext.user?.organizations?.find((organization) => String(organization.id) === String(activeOrganizationId));
    return organization ? { ...organization, name: organization.name || 'Organization' } : null;
  }, [activeOrganization, activeOrganizationId, authContext.user?.organizations, guestOrganizations, organizations]);
  const menuOrganizations = useMemo(() => {
    const entries = [
      ...organizations.map((organization) => ({ organization, asGuest: false })),
      ...guestOrganizations.map((organization) => ({ organization, asGuest: true })),
    ];
    if (
      currentOrganization &&
      !entries.some(({ organization }) => String(organization.id) === String(currentOrganization.id))
    ) {
      entries.unshift({ organization: currentOrganization, asGuest: false });
    }
    return entries.sort(
      (a, b) =>
        Number(String(b.organization.id) === String(activeOrganizationId)) -
        Number(String(a.organization.id) === String(activeOrganizationId))
    );
  }, [activeOrganizationId, currentOrganization, guestOrganizations, organizations]);

  async function handleSwitchOrganization(organizationId: string | number, asGuest = false) {
    if (!organizationId || String(organizationId) === String(activeOrganizationId)) {
      return;
    }

    try {
      setIsSwitchingOrganization(true);
      viewContext?.setLoading?.(true);
      if (asGuest) {
        await setAdminActiveOrganization(String(organizationId));
      } else {
        await setActiveOrganization(organizationId);
      }
      await authContext.refreshSession?.();
      navigate('/dashboard');
    } finally {
      setIsSwitchingOrganization(false);
      viewContext?.setLoading?.(false);
    }
  }

  async function handleManageOrganization(organizationId: string | number, asGuest = false) {
    try {
      setIsSwitchingOrganization(true);
      if (String(organizationId) !== String(activeOrganizationId)) {
        if (asGuest) {
          await setAdminActiveOrganization(String(organizationId));
        } else {
          await setActiveOrganization(organizationId);
        }
        await authContext.refreshSession?.();
      }
      navigate('/account/organization');
    } finally {
      setIsSwitchingOrganization(false);
    }
  }

  async function handleAcceptInvitation(invitation: OrganizationInvitation) {
    try {
      setAcceptingInvitationId(invitation.id);
      await acceptOrganizationInvitation(invitation.id);
      await setActiveOrganization(invitation.organizationId);
      const nextOrganizations = await listOrganizations();
      setOrganizations(Array.isArray(nextOrganizations) ? nextOrganizations : []);
      setInvitations((current) => current.filter((item) => item.id !== invitation.id));
      await authContext.refreshSession?.();
      navigate('/dashboard');
      viewContext.notification?.show('Invitation accepted', 'success', true, 'toast');
    } catch {
      viewContext.notification?.show('Unable to accept invitation', 'error', true, 'toast');
    } finally {
      setAcceptingInvitationId(null);
    }
  }

  return (
    <>
      <OrganizationSwitch
        userName={authContext.user?.name}
        activeOrganizationId={activeOrganizationId}
        organizations={menuOrganizations.map(({ organization, asGuest }) => ({ ...organization, asGuest }))}
        {...(canSwitchById
          ? { guestOrganizations: guestOrganizations.map((organization) => ({ ...organization, asGuest: true })) }
          : {})}
        invitations={invitations}
        isOrganizationsLoading={isOrganizationsLoading}
        isSwitchingOrganization={isSwitchingOrganization}
        acceptingInvitationId={acceptingInvitationId}
        onSwitchOrganization={handleSwitchOrganization}
        onManageOrganization={authContext.permission?.owner ? handleManageOrganization : undefined}
        onAcceptInvitation={(invitation: OrganizationSwitchInvitation) =>
          handleAcceptInvitation(invitation as OrganizationInvitation)
        }
        onCreateOrganization={() => setCreateOrganizationOpen(true)}
        className={className}
        nameClassName={nameClassName}
        switchButtonClassName={switchButtonClassName}
      />

      <CreateOrganizationModal
        open={createOrganizationOpen}
        onClose={() => setCreateOrganizationOpen(false)}
        previousOrganizations={organizations}
        onCreated={(nextOrganizations) => setOrganizations(nextOrganizations as Organization[])}
        redirectTo="/register/plan"
      />
    </>
  );
}
